import React from "react";
import { Form } from "react-bootstrap";
import { getAvailableLanguages } from "../Languages";

/**
 * The 33 article translation languages supported by the backend
 * (kept in sync with `ARTICLE_LANGUAGES` in Kjuup-BE
 * `api/services/TranslationService.js`).
 *
 * We reuse the same registry the rest of the app uses for its own UI
 * languages so adding a new locale only requires one update.
 */
export const ARTICLE_LANGUAGE_OPTIONS = getAvailableLanguages();

export const DEFAULT_ARTICLE_LANGUAGE = "de";

export function getArticleLanguageLabel(code) {
  if (!code) return "";
  const found = ARTICLE_LANGUAGE_OPTIONS.find(
    (opt) => opt.code === String(code).toLowerCase()
  );
  if (!found) return code;
  return found.nativeName || found.name;
}

/**
 * Single-select dropdown for the article's authoring (source) language.
 *
 * The article's title/description will not be translated into the selected
 * language - the original wording is kept on the article itself and served
 * back when readers request that language.
 *
 * Props:
 *  - value: current language code (e.g. "de")
 *  - onChange: (code) => void
 *  - disabled: boolean
 *  - id: html id (optional)
 *  - className: extra classes for the underlying <Form.Control> (optional)
 */
export default function ArticleLanguagePicker({
  value,
  onChange,
  disabled,
  id,
  className,
}) {
  const safeValue =
    value &&
    ARTICLE_LANGUAGE_OPTIONS.some((opt) => opt.code === String(value).toLowerCase())
      ? String(value).toLowerCase()
      : DEFAULT_ARTICLE_LANGUAGE;

  return (
    <Form.Control
      as="select"
      id={id}
      value={safeValue}
      onChange={(e) => onChange && onChange(e.target.value)}
      disabled={!!disabled}
      className={className}
    >
      {ARTICLE_LANGUAGE_OPTIONS.map(({ code, name, nativeName }) => (
        <option key={code} value={code}>
          {nativeName ? `${nativeName}` : name}
        </option>
      ))}
    </Form.Control>
  );
}
